import { useState, useEffect } from 'react';
import { api } from './api';

function AdminPage() {
    const [politicians, setPoliticians] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingPolitician, setEditingPolitician] = useState(null);
    const [newPolitician, setNewPolitician] = useState({
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        party: 'Democrat',
        branch: 'house',
        state: '',
        district: '',
        position: '',
        yearsInOffice: '',
        officialWebsite: '',
        socialMedia: {
            twitter: '',
            instagram: '',
            tiktok: ''
        }
    });
    const [newHealthData, setNewHealthData] = useState({
        medicalConditions: [],
        recentHealthEvents: [],
        publicDisclosures: [],
        ageRiskFactors: [],
        fitnessLevel: 'medium'
    });

    useEffect(() => {
        fetchPoliticians();
    }, []);

    const fetchPoliticians = async () => {
        const data = await api.getAllPoliticians();
        setPoliticians(data);
    };

    const handleAddPolitician = async (e) => {
        e.preventDefault();

        try {
            const response = await fetch('http://localhost:5000/api/admin/politicians', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    politician: newPolitician,
                    healthData: newHealthData
                })
            });

            if (response.ok) {
                const result = await response.json();
                console.log('Politician added:', result);
                fetchPoliticians();
                setShowAddForm(false);
                resetForms();
                alert('Politician added successfully!');
            } else {
                const error = await response.json();
                console.error('Server error:', error);
                alert(`Error: ${error.error || 'Failed to add politician'}`);
            }
        } catch (error) {
            console.error('Error adding politician:', error);
            alert('Network error: Could not connect to server');
        }
    };

    const resetForms = () => {
        setNewPolitician({
            firstName: '',
            lastName: '',
            dateOfBirth: '',
            party: 'Democrat',
            branch: 'house',
            state: '',
            district: '',
            position: '',
            yearsInOffice: '',
            officialWebsite: '',
            socialMedia: {
                twitter: '',
                instagram: '',
                tiktok: ''
            }
        });
        setNewHealthData({
            medicalConditions: [],
            recentHealthEvents: [],
            publicDisclosures: [],
            ageRiskFactors: [],
            fitnessLevel: 'medium'
        });
    };

    const addCondition = (condition) => {
        if (condition && !newHealthData.medicalConditions.includes(condition)) {
            setNewHealthData({
                ...newHealthData,
                medicalConditions: [...newHealthData.medicalConditions, condition]
            });
        }
    };

    const removeCondition = (condition) => {
        setNewHealthData({
            ...newHealthData,
            medicalConditions: newHealthData.medicalConditions.filter(c => c !== condition)
        });
    };

    const addHealthEvent = () => {
        const event = {
            date: document.getElementById('eventDate').value,
            condition: document.getElementById('eventCondition').value,
            severity: document.getElementById('eventSeverity').value,
            recovered: document.getElementById('eventRecovered').value === 'true',
            description: document.getElementById('eventDescription').value
        };

        if (event.date && event.condition) {
            setNewHealthData({
                ...newHealthData,
                recentHealthEvents: [...newHealthData.recentHealthEvents, event]
            });

            // Clear form
            document.getElementById('eventDate').value = '';
            document.getElementById('eventCondition').value = '';
            document.getElementById('eventDescription').value = '';
        }
    };

    const commonConditions = [
        'heart_disease', 'diabetes_type2', 'hypertension', 'arthritis', 'cancer',
        'stroke', 'dementia', 'osteoporosis', 'high_cholesterol', 'chronic_fatigue',
        'fibromyalgia', 'prostate_cancer', 'mild_cognitive_impairment'
    ];

    const riskFactors = [
        'cognitive_decline', 'mobility_concerns', 'high_cardiovascular_risk',
        'bone_density_loss', 'fall_risk', 'diabetic_complications', 'cancer_risk',
        'age_related_decline', 'advanced_age', 'chronic_condition_management'
    ];

    return (
        <div className="page">
            <div className="admin-container">
                <h2>🔧 Admin Panel</h2>
                <p>Manage politician data and health information</p>

                <div className="admin-stats">
                    <div className="stat-box">
                        <span className="stat-number">{politicians.length}</span>
                        <span className="stat-label">Total Politicians</span>
                    </div>
                    <div className="stat-box">
                        <span className="stat-number">
                            {politicians.filter(p => p.healthScore < 40).length}
                        </span>
                        <span className="stat-label">Critical Health</span>
                    </div>
                </div>

                <div className="admin-actions">
                    <button
                        className="admin-button primary"
                        onClick={() => setShowAddForm(!showAddForm)}
                    >
                        {showAddForm ? 'Cancel' : '+ Add New Politician'}
                    </button>

                    <button className="admin-button secondary">
                        📥 Import CSV
                    </button>

                    <button className="admin-button secondary">
                        📊 Export Data
                    </button>
                </div>

                {showAddForm && (
                    <div className="add-politician-form">
                        <h3>Add New Politician</h3>
                        <form onSubmit={handleAddPolitician}>

                            {/* Basic Info */}
                            <div className="form-section">
                                <h4>Basic Information</h4>
                                <div className="form-grid">
                                    <input
                                        type="text"
                                        placeholder="First Name"
                                        value={newPolitician.firstName}
                                        onChange={(e) => setNewPolitician({ ...newPolitician, firstName: e.target.value })}
                                        required
                                    />

                                    <input
                                        type="text"
                                        placeholder="Last Name"
                                        value={newPolitician.lastName}
                                        onChange={(e) => setNewPolitician({ ...newPolitician, lastName: e.target.value })}
                                        required
                                    />

                                    <input
                                        type="date"
                                        placeholder="Date of Birth"
                                        value={newPolitician.dateOfBirth}
                                        onChange={(e) => setNewPolitician({ ...newPolitician, dateOfBirth: e.target.value })}
                                        required
                                    />

                                    <select
                                        value={newPolitician.party}
                                        onChange={(e) => setNewPolitician({ ...newPolitician, party: e.target.value })}
                                    >
                                        <option value="Democrat">Democrat</option>
                                        <option value="Republican">Republican</option>
                                        <option value="Independent">Independent</option>
                                    </select>

                                    <select
                                        value={newPolitician.branch}
                                        onChange={(e) => setNewPolitician({ ...newPolitician, branch: e.target.value })}
                                    >
                                        <option value="house">House</option>
                                        <option value="senate">Senate</option>
                                        <option value="executive">Executive</option>
                                    </select>

                                    <input
                                        type="text"
                                        placeholder="State"
                                        value={newPolitician.state}
                                        onChange={(e) => setNewPolitician({ ...newPolitician, state: e.target.value })}
                                    />

                                    {newPolitician.branch === 'house' && (
                                        <input
                                            type="text"
                                            placeholder="District"
                                            value={newPolitician.district}
                                            onChange={(e) => setNewPolitician({ ...newPolitician, district: e.target.value })}
                                        />
                                    )}

                                    {newPolitician.branch === 'executive' && (
                                        <input
                                            type="text"
                                            placeholder="Position"
                                            value={newPolitician.position}
                                            onChange={(e) => setNewPolitician({ ...newPolitician, position: e.target.value })}
                                        />
                                    )}

                                    <input
                                        type="number"
                                        placeholder="Years in Office"
                                        value={newPolitician.yearsInOffice}
                                        onChange={(e) => setNewPolitician({ ...newPolitician, yearsInOffice: e.target.value })}
                                    />
                                </div>
                            </div>

                            {/* Websites & Social Media */}
                            <div className="form-section">
                                <h4>Official Website & Social Media</h4>
                                <div className="form-grid">
                                    <input
                                        type="url"
                                        placeholder="Official Website (e.g., https://johnsmith.house.gov)"
                                        value={newPolitician.officialWebsite}
                                        onChange={(e) => setNewPolitician({ ...newPolitician, officialWebsite: e.target.value })}
                                    />

                                    <input
                                        type="url"
                                        placeholder="Twitter URL (e.g., https://twitter.com/username)"
                                        value={newPolitician.socialMedia.twitter}
                                        onChange={(e) => setNewPolitician({
                                            ...newPolitician,
                                            socialMedia: { ...newPolitician.socialMedia, twitter: e.target.value }
                                        })}
                                    />

                                    <input
                                        type="url"
                                        placeholder="Instagram URL (e.g., https://instagram.com/username)"
                                        value={newPolitician.socialMedia.instagram}
                                        onChange={(e) => setNewPolitician({
                                            ...newPolitician,
                                            socialMedia: { ...newPolitician.socialMedia, instagram: e.target.value }
                                        })}
                                    />

                                    <input
                                        type="url"
                                        placeholder="TikTok URL (e.g., https://tiktok.com/@username)"
                                        value={newPolitician.socialMedia.tiktok}
                                        onChange={(e) => setNewPolitician({
                                            ...newPolitician,
                                            socialMedia: { ...newPolitician.socialMedia, tiktok: e.target.value }
                                        })}
                                    />
                                </div>
                            </div>

                            {/* Medical Conditions */}
                            <div className="form-section">
                                <h4>Medical Conditions</h4>
                                <div className="conditions-selector">
                                    {commonConditions.map(condition => (
                                        <button
                                            key={condition}
                                            type="button"
                                            className={`condition-tag ${newHealthData.medicalConditions.includes(condition) ? 'selected' : ''}`}
                                            onClick={() => newHealthData.medicalConditions.includes(condition)
                                                ? removeCondition(condition)
                                                : addCondition(condition)
                                            }
                                        >
                                            {condition.replace(/_/g, ' ')}
                                        </button>
                                    ))}
                                </div>

                                {newHealthData.medicalConditions.length > 0 && (
                                    <div className="selected-conditions">
                                        <strong>Selected:</strong> {newHealthData.medicalConditions.join(', ')}
                                    </div>
                                )}
                            </div>

                            {/* Health Events */}
                            <div className="form-section">
                                <h4>Recent Health Events</h4>
                                <div className="health-event-form">
                                    <input type="date" id="eventDate" placeholder="Date" />
                                    <input type="text" id="eventCondition" placeholder="Health Event" />
                                    <select id="eventSeverity">
                                        <option value="low">Low Severity</option>
                                        <option value="medium">Medium Severity</option>
                                        <option value="high">High Severity</option>
                                    </select>
                                    <select id="eventRecovered">
                                        <option value="true">Recovered</option>
                                        <option value="false">Not Recovered</option>
                                    </select>
                                    <input type="text" id="eventDescription" placeholder="Description" />
                                    <button type="button" onClick={addHealthEvent}>Add Event</button>
                                </div>

                                {newHealthData.recentHealthEvents.length > 0 && (
                                    <div className="added-events">
                                        {newHealthData.recentHealthEvents.map((event, index) => (
                                            <div key={index} className="event-preview">
                                                <strong>{event.condition}</strong> - {event.date} ({event.severity})
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Risk Factors */}
                            <div className="form-section">
                                <h4>Age-Related Risk Factors</h4>
                                <div className="risk-factors-selector">
                                    {riskFactors.map(risk => (
                                        <button
                                            key={risk}
                                            type="button"
                                            className={`risk-tag ${newHealthData.ageRiskFactors.includes(risk) ? 'selected' : ''}`}
                                            onClick={() => {
                                                if (newHealthData.ageRiskFactors.includes(risk)) {
                                                    setNewHealthData({
                                                        ...newHealthData,
                                                        ageRiskFactors: newHealthData.ageRiskFactors.filter(r => r !== risk)
                                                    });
                                                } else {
                                                    setNewHealthData({
                                                        ...newHealthData,
                                                        ageRiskFactors: [...newHealthData.ageRiskFactors, risk]
                                                    });
                                                }
                                            }}
                                        >
                                            {risk.replace(/_/g, ' ')}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Fitness Level */}
                            <div className="form-section">
                                <h4>Fitness Level</h4>
                                <select
                                    value={newHealthData.fitnessLevel}
                                    onChange={(e) => setNewHealthData({ ...newHealthData, fitnessLevel: e.target.value })}
                                >
                                    <option value="very_low">Very Low</option>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="very_high">Very High</option>
                                </select>
                            </div>

                            <button type="submit" className="submit-button">
                                Add Politician
                            </button>
                        </form>
                    </div>
                )}

                {/* Existing Politicians List */}
                <div className="politicians-management">
                    <h3>Existing Politicians</h3>
                    <div className="politicians-table">
                        {politicians.map(politician => (
                            <div key={politician.id} className="politician-row">
                                <div className="politician-info">
                                    <strong>{politician.name}</strong>
                                    <span>{politician.party} - {politician.age} years</span>
                                </div>
                                <div className="politician-health">
                                    <span className={`health-score score-${Math.floor(politician.healthScore / 20)}`}>
                                        {politician.healthScore}
                                    </span>
                                </div>
                                <div className="politician-actions">
                                    <button onClick={() => setEditingPolitician(politician.id)}>Edit</button>
                                    <button className="danger">Delete</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminPage;